import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as XLSX from 'xlsx';
import { CustomerOption, CustomerProjectsService, ProjectOption } from '../../core/services/customer-projects.service';
import {
  FixedPriceProject,
  FixedPriceProjectsService,
} from '../../core/services/fixed-price-projects.service';
import { ModalService } from '../../core/services/modal.service';
import { FixedPriceProjectTimerecordsModalComponent } from '../../shared/components/modal/fixed-price-project-timerecords-modal.component';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';

type FixedPriceColumnType = 'text' | 'date' | 'number' | 'percent';

interface FixedPriceColumn {
  label: string;
  key: keyof FixedPriceProject;
  type: FixedPriceColumnType;
}

function applyWorksheetNumberFormats(
  worksheet: XLSX.WorkSheet,
  columns: FixedPriceColumn[],
  rowCount: number
): void {
  for (let row = 1; row <= rowCount; row += 1) {
    columns.forEach((column, columnIndex) => {
      if (column.type !== 'number' && column.type !== 'percent') {
        return;
      }

      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: columnIndex })];
      if (!cell || typeof cell.v !== 'number') {
        return;
      }
      cell.t = 'n';
      cell.z = Number.isInteger(cell.v) ? '0' : '0.00';
    });
  }
}

@Component({
  selector: 'app-ng-fixed-price-projects',
  template: require('./ng-fixed-price-projects.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-fixed-price-projects.component.scss').default],
  standalone: false,
})
export class NgFixedPriceProjectsComponent implements OnInit {
  customers: CustomerOption[] = [];
  selectedCustomer: CustomerOption | null = null;
  selectedProject: ProjectOption | null = null;
  includeCompleted = false;
  projects: FixedPriceProject[] = [];
  hasLoaded = false;
  isLoading = false;
  errorMessage = '';

  readonly columns: FixedPriceColumn[] = [
    { label: 'Kund', key: 'customername', type: 'text' },
    { label: 'Projekt', key: 'project_name', type: 'text' },
    { label: 'PO', key: 'extprojectid', type: 'text' },
    { label: 'Start', key: 'start_date', type: 'date' },
    { label: 'Stopp', key: 'stop_date', type: 'date' },
    { label: 'Budget timmar', key: 'budget_hours', type: 'number' },
    { label: 'Budget kr', key: 'budget_money', type: 'number' },
    { label: 'Rapporterade timmar', key: 'used_hours', type: 'number' },
    { label: 'Rapporterade kr', key: 'used_money', type: 'number' },
    { label: 'Fakturerade timmar', key: 'invoiced_hours', type: 'number' },
    { label: 'Fakturerade kr', key: 'invoiced_money', type: 'number' },
    { label: 'Kvar timmar', key: 'remaining_hours', type: 'number' },
    { label: 'Kvar kr', key: 'remaining_money', type: 'number' },
    { label: 'Förbrukning', key: 'used_percent', type: 'percent' },
  ];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(FixedPriceProjectsService) private fixedPriceProjectsService: FixedPriceProjectsService,
    @Inject(CustomerProjectsService) private customerProjectsService: CustomerProjectsService,
    @Inject(ModalService) private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.fetchCustomerProjects();
  }

  get projectsForCustomer(): ProjectOption[] {
    return this.selectedCustomer?.projects ?? [];
  }

  onCustomerChange(): void {
    this.selectedProject = null;
  }

  fetchReport(): void {
    this.errorMessage = '';
    this.isLoading = true;
    this.hasLoaded = true;

    this.fixedPriceProjectsService
      .getProjects({
        customerId: this.selectedCustomer?.id ?? null,
        projectId: this.selectedProject?.id ?? null,
        includeCompleted: this.includeCompleted,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (projects) => {
          this.projects = projects;
          this.isLoading = false;
        },
        error: () => {
          this.projects = [];
          this.errorMessage = 'Kunde inte hämta fastprisprojekten.';
          this.isLoading = false;
        },
      });
  }

  getCellValue(project: FixedPriceProject, column: FixedPriceColumn): string | number | null {
    const value = project[column.key];
    return typeof value === 'string' || typeof value === 'number' ? value : null;
  }

  getExportRows(): Array<Array<string | number | null>> {
    return this.projects.map((project) =>
      this.columns.map((column) => this.getCellValue(project, column))
    );
  }

  exportToExcel(): void {
    const rows = [
      this.columns.map((column) => column.label),
      ...this.getExportRows(),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    removeBlankStringCells(worksheet);
    applyWorksheetNumberFormats(worksheet, this.columns, this.projects.length);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Fastprisprojekt');
    XLSX.writeFile(workbook, 'fastprisprojekt.xlsx');
  }

  openTimerecords(project: FixedPriceProject): void {
    this.errorMessage = '';

    this.fixedPriceProjectsService
      .getTimerecords(project.project_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.modalService.show(FixedPriceProjectTimerecordsModalComponent, {
            title: `Tidrapport: ${project.project_name}`,
            rows,
          });
        },
        error: () => {
          this.errorMessage = 'Kunde inte hämta tidrapporten.';
        },
      });
  }

  private fetchCustomerProjects(): void {
    this.customerProjectsService
      .getCustomerProjects()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (customers) => {
          this.customers = customers;
        },
        error: () => {
          this.customers = [];
          this.errorMessage = 'Kunde inte hämta kund- och projektfilter.';
        },
      });
  }
}
